import type { ApolloClient } from '@frameio/graphql-client';
import type {
  CompleteAccountUserFragment,
  Membership,
  ResourceMembershipFragment,
} from '@frameio/graphql-client/generated';
import {
  CompleteAccountUser,
  ResourceMembership,
} from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';

/**
 * A util to read a membership fragment off the cache.
 * Not reactive, but useful for callbacks or other contexts where a hook cannot be used.
 * Passes locale so Apollo can resolve parameterized Role fields (name, description) from cache.
 */
export function readMembershipFragment(
  client: ApolloClient<any>,
  id: Membership['id']
) {
  const locale = toSupportedLocaleOrDefault(intl().locale);

  const membershipFragment = client.readFragment<ResourceMembershipFragment>(
    {
      fragment: ResourceMembership,
      fragmentName: 'ResourceMembership',
      id: client.cache.identify({ __typename: 'Membership', id }),
      variables: { locale },
    },
    true
  );

  const accountUserFragment = client.readFragment<CompleteAccountUserFragment>(
    {
      fragment: CompleteAccountUser,
      fragmentName: 'CompleteAccountUser',
      id: client.cache.identify({
        __typename: 'AccountUser',
        id,
      }),
    },
    true
  );

  return {
    member: membershipFragment,
    accountUser: accountUserFragment,
  };
}
